import React from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Check,
  ChevronDown,
  Clock,
  Plus,
  Search,
  Shield,
  UserCircle2,
  Users,
  X,
} from 'lucide-react';
import {
  loadAssignDepartments,
  loadAssignDuty,
  loadAssignTeams,
  loadAssignUsers,
} from './AssignPersonnelModal';
import { DutyBadge } from './PersonnelOfferPrompt';
import { expandDistributionRecipients, parseTimestamp } from '@/uitilites/utility';

const DUTY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in_duty', label: 'On duty' },
  { value: 'not_in_duty', label: 'Off duty' },
  { value: 'no_response', label: 'No response' },
];

const dutyStatus = (info) => {
  if (info?.status === 'in_duty') return 'in_duty';
  if (info?.status === 'not_in_duty') return 'not_in_duty';
  return 'no_response';
};

const dutyDurationLabel = (info) => {
  if (info?.status !== 'in_duty' || !info?.responded_at) return '';
  const at = parseTimestamp(info.responded_at);
  if (!at || Number.isNaN(at.getTime())) return '';
  const minutes = Math.floor((Date.now() - at.getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return '';
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const refKey = (row) => `${row?.type}:${row?.id}`;

export default function StaffingTargetPicker({
  open,
  title = 'Choose staffing targets',
  description = '',
  selected = [],
  onSelect,
  onClose,
  dark = false,
}) {
  const [users, setUsers] = React.useState([]);
  const [rawUsers, setRawUsers] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [teams, setTeams] = React.useState([]);
  const [duty, setDuty] = React.useState({});
  const [tab, setTab] = React.useState('users');
  const [query, setQuery] = React.useState('');
  const [dutyFilter, setDutyFilter] = React.useState('all');
  const [expanded, setExpanded] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTab('users');
    setQuery('');
    setDutyFilter('all');
    setExpanded({});
    setLoading(true);
    Promise.all([
      loadAssignUsers(),
      loadAssignDepartments(),
      loadAssignTeams(),
      loadAssignDuty(),
    ]).then(([userResult, departmentResult, teamResult, dutyResult]) => {
      setUsers(userResult?.display || []);
      setRawUsers(userResult?.raw || []);
      setDepartments(departmentResult || []);
      setTeams(teamResult || []);
      setDuty(dutyResult || {});
    }).finally(() => setLoading(false));
  }, [open]);

  const selectedKeys = React.useMemo(
    () => new Set((selected || []).map(refKey)),
    [selected],
  );

  const passesDuty = React.useCallback((userId) => {
    if (dutyFilter === 'all') return true;
    return dutyStatus(duty[String(userId)] || {}) === dutyFilter;
  }, [duty, dutyFilter]);

  const membersFor = React.useCallback((type, id, name) => {
    if (type !== 'team') {
      return expandDistributionRecipients({
        distributionList: [{ type, id, name }],
        appUsers: rawUsers,
        departments,
        teams,
      }).filter((member) => member?.userId);
    }

    const team = teams.find((row) => String(row?.id) === String(id));
    const byId = new Map();
    (team?.crews || []).forEach((crew) => {
      expandDistributionRecipients({
        distributionList: [{ type: 'crew', id: crew.id, name: crew.crew_name || crew.name }],
        appUsers: rawUsers,
        departments,
        teams,
      }).forEach((member) => {
        if (member?.userId) byId.set(String(member.userId), member);
      });
    });
    rawUsers.forEach((user) => {
      const userTeamId = user?.team_id || user?.teamId;
      if (String(userTeamId || '') !== String(id) || !user?.id || user?.is_active === false) return;
      byId.set(String(user.id), {
        userId: String(user.id),
        userName: user.name || user.full_name || user.email || 'User',
        email: user.email || '',
      });
    });
    return [...byId.values()];
  }, [departments, rawUsers, teams]);

  const crews = React.useMemo(() => {
    const result = [];
    teams.forEach((team) => {
      if (team?.id && team?.is_active !== false) {
        result.push({
          type: 'team',
          id: team.id,
          name: team.team_name || team.name || 'Team',
          sub: 'Team',
        });
      }
      (team?.crews || []).forEach((crew) => {
        if (!crew?.id || crew?.is_active === false) return;
        result.push({
          type: 'crew',
          id: crew.id,
          name: crew.crew_name || crew.name || 'Crew',
          sub: team.team_name || team.name || 'Team',
        });
      });
    });
    return result;
  }, [teams]);

  const q = query.trim().toLowerCase();
  const rank = (id) => dutyStatus(duty[String(id)] || {}) === 'in_duty'
    ? 0
    : dutyStatus(duty[String(id)] || {}) === 'no_response' ? 1 : 2;
  const visibleUsers = users
    .filter((user) => passesDuty(user.id))
    .filter((user) => !q || `${user.name} ${user.position || ''}`.toLowerCase().includes(q))
    .sort((a, b) => rank(a.id) - rank(b.id) || a.name.localeCompare(b.name));

  const makeGroup = (type, id, name, sub = '') => {
    const allMembers = membersFor(type, id, name);
    const visibleMembers = allMembers.filter((member) => passesDuty(member.userId));
    return { type, id, name, sub, allMembers, visibleMembers };
  };

  const visibleDepartments = departments
    .filter((department) => department?.id)
    .map((department) => makeGroup(
      'department',
      department.id,
      department.department_name || department.name || 'Department',
    ))
    .filter((group) => !q
      || group.name.toLowerCase().includes(q)
      || group.allMembers.some((member) => `${member.userName || ''} ${member.email || ''}`.toLowerCase().includes(q)));

  const visibleCrews = crews
    .map((group) => makeGroup(group.type, group.id, group.name, group.sub))
    .filter((group) => !q
      || `${group.name} ${group.sub}`.toLowerCase().includes(q)
      || group.allMembers.some((member) => `${member.userName || ''} ${member.email || ''}`.toLowerCase().includes(q)));

  if (!open) return null;

  const add = (row) => {
    if (!row?.id || selectedKeys.has(refKey(row))) return;
    onSelect?.({ type: row.type, id: String(row.id), name: row.name });
  };

  const DutyInfo = ({ userId }) => {
    const info = duty[String(userId)] || {};
    const duration = dutyDurationLabel(info);
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5">
        {duration && (
          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-600">
            <Clock className="h-2.5 w-2.5" /> {duration}
          </span>
        )}
        <DutyBadge status={info?.status} />
      </span>
    );
  };

  const SelectButton = ({ row, label = 'Add' }) => {
    const isSelected = selectedKeys.has(refKey(row));
    return (
      <button
        type="button"
        disabled={isSelected}
        onClick={() => add(row)}
        className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white transition-colors ${isSelected
          ? 'bg-emerald-600'
          : dark ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-violet-600 hover:bg-violet-700'}`}
      >
        {isSelected ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        {isSelected ? 'Added' : label}
      </button>
    );
  };

  const GroupRow = ({ group }) => {
    const key = refKey(group);
    const isExpanded = !!expanded[key];
    const Icon = group.type === 'department' ? Building2 : group.type === 'team' ? Users : Shield;
    return (
      <div className={`overflow-hidden rounded-lg border ${dark ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className={`flex items-center gap-2 px-2.5 py-2 ${dark ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-800'}`}>
          <button
            type="button"
            onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'} ${dark ? 'text-slate-500' : 'text-slate-400'}`} />
            <Icon className={`h-4 w-4 shrink-0 ${dark ? 'text-cyan-400' : 'text-violet-600'}`} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold">{group.name}</span>
              <span className={`block truncate text-[9px] font-semibold uppercase tracking-wide ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {group.type}{group.sub ? ` · ${group.sub}` : ''}
              </span>
            </span>
          </button>
          <span className={`shrink-0 text-[10px] font-semibold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {group.visibleMembers.length} members
          </span>
          <SelectButton row={group} label="Add group" />
        </div>
        {isExpanded && (
          <div className={`space-y-0.5 border-t p-1 ${dark ? 'border-slate-700 bg-slate-950/70' : 'border-slate-100 bg-slate-50/80'}`}>
            {group.visibleMembers.map((member) => {
              const row = { type: 'user', id: member.userId, name: member.userName || member.email || 'User' };
              return (
                <div key={member.userId} className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${dark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-white'}`}>
                  <UserCircle2 className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-cyan-400' : 'text-blue-500'}`} />
                  <span className="min-w-0 flex-1 truncate text-xs">{row.name}</span>
                  <DutyInfo userId={member.userId} />
                  <SelectButton row={row} />
                </div>
              );
            })}
            {group.visibleMembers.length === 0 && (
              <p className={`px-2 py-2 text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                No members match this duty filter.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'users', label: 'Users' },
    { id: 'departments', label: 'Departments' },
    { id: 'crews', label: 'Crews' },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className={`flex max-h-[86vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border shadow-2xl ${dark ? 'border-slate-700 bg-slate-900 shadow-black/60' : 'border-slate-200 bg-white'}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
          <div>
            <h3 className={`text-sm font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h3>
            <p className={`mt-0.5 text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {description || 'Choose users, departments, teams, or crews.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className={`rounded-md p-1.5 ${dark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'}`}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className={`space-y-2 border-b px-4 py-3 ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${dark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
            <Search className={`h-3.5 w-3.5 ${dark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={tab === 'users' ? 'Search users…' : tab === 'departments' ? 'Search departments…' : 'Search teams and crews…'}
              className={`w-full bg-transparent text-sm outline-none ${dark ? 'text-slate-100 placeholder:text-slate-500' : 'text-slate-800'}`}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className={`flex items-center gap-0.5 rounded-lg p-0.5 ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}>
              {tabs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={`rounded-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide ${tab === item.id
                    ? dark ? 'bg-cyan-600 text-white' : 'bg-white text-violet-700 shadow-sm'
                    : dark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {DUTY_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setDutyFilter(filter.value)}
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${dutyFilter === filter.value
                    ? dark ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-violet-600 bg-violet-600 text-white'
                    : dark ? 'border-slate-600 text-slate-400' : 'border-slate-200 text-slate-500'}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
          {loading && <p className={`px-3 py-5 text-center text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Loading personnel…</p>}
          {!loading && tab === 'users' && visibleUsers.map((user) => {
            const row = { type: 'user', id: user.id, name: user.name };
            return (
              <div key={user.id} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 ${dark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-800 hover:bg-violet-50'}`}>
                <UserCircle2 className={`h-4 w-4 shrink-0 ${dark ? 'text-cyan-400' : 'text-blue-500'}`} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{user.name}</span>
                <DutyInfo userId={user.id} />
                <SelectButton row={row} />
              </div>
            );
          })}
          {!loading && tab === 'users' && visibleUsers.length === 0 && <p className={`px-3 py-5 text-center text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No users match this filter.</p>}
          {!loading && tab === 'departments' && visibleDepartments.map((group) => <GroupRow key={refKey(group)} group={group} />)}
          {!loading && tab === 'departments' && visibleDepartments.length === 0 && <p className={`px-3 py-5 text-center text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No departments found.</p>}
          {!loading && tab === 'crews' && visibleCrews.map((group) => <GroupRow key={refKey(group)} group={group} />)}
          {!loading && tab === 'crews' && visibleCrews.length === 0 && <p className={`px-3 py-5 text-center text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No teams or crews found.</p>}
        </div>

        <div className={`flex items-center justify-between gap-3 border-t px-4 py-3 ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
          <span className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{selected.length} selected</span>
          <button type="button" onClick={onClose} className={`rounded-lg px-4 py-2 text-xs font-bold text-white ${dark ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-violet-600 hover:bg-violet-700'}`}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
